/**
 * Перевод ошибок API по коду (docs/contracts/backend-conventions.md: `errors.<CODE>`).
 * Порядок: перевод веба errors:<CODE> → текст сервера (он уже на языке X-Locale) → общий текст по статусу.
 */
import { ApiError } from '@dodoc/api-client';
import type { i18n as I18nInstance, TFunction } from 'i18next';
import { retryAfterSeconds } from './http';

export type ErrorKind = 'network' | 'unauthorized' | 'mfa' | 'forbidden' | 'notFound' | 'validation' | 'conflict' | 'rateLimited' | 'unavailable' | 'server' | 'unknown';

export interface ErrorInfo {
  kind: ErrorKind;
  code: string;
  status: number;
  title: string;
  description: string;
  requestId: string | null;
  retryAfter: number | null;
  params: Record<string, unknown>;
}

export function errorKind(error: unknown): ErrorKind {
  if (!(error instanceof ApiError)) return 'unknown';
  if (error.status === 0) return 'network';
  if (error.code === 'MFA_REQUIRED') return 'mfa';
  if (error.status === 401) return 'unauthorized';
  if (error.status === 403) return 'forbidden';
  if (error.status === 404) return 'notFound';
  if (error.status === 409) return 'conflict';
  if (error.status === 429) return 'rateLimited';
  if (error.status === 503 || error.status === 502 || error.status === 504 || error.code === 'RK_UNAVAILABLE') return 'unavailable';
  if (error.status === 400 || error.status === 422) return 'validation';
  if (error.status >= 500) return 'server';
  return 'unknown';
}

const FALLBACK_CODE: Record<ErrorKind, string> = {
  network: 'NETWORK_ERROR',
  unauthorized: 'UNAUTHORIZED',
  mfa: 'MFA_REQUIRED',
  forbidden: 'FORBIDDEN',
  notFound: 'NOT_FOUND',
  validation: 'VALIDATION_FAILED',
  conflict: 'CONFLICT',
  rateLimited: 'RATE_LIMITED',
  unavailable: 'SERVICE_UNAVAILABLE',
  server: 'INTERNAL',
  unknown: 'UNKNOWN',
};

/** Неверный телефон или пароль на форме входа — тоже 401, но это не истёкший вход: свой заголовок вместо «Нужно войти снова». */
const CREDENTIAL_CODES: readonly string[] = ['IDENTITY_INVALID_CREDENTIALS'];

/** Понятное описание ошибки: что случилось (title) и что делать (description). */
export function describeError(error: unknown, t: TFunction, i18n: Pick<I18nInstance, 'exists'>): ErrorInfo {
  const kind = errorKind(error);
  const apiError = error instanceof ApiError ? error : null;
  const code = apiError?.code ?? 'UNKNOWN';
  const params = apiError?.params ?? {};
  const retryAfter = retryAfterSeconds(error);
  let description: string;
  if (i18n.exists(`errors:${code}`)) {
    description = t(`errors:${code}`, params);
  } else if (apiError && apiError.message && !/^HTTP \d+$/.test(apiError.message) && apiError.message !== apiError.code) {
    description = apiError.message;
  } else {
    description = t(`errors:${FALLBACK_CODE[kind]}`);
  }
  if (kind === 'rateLimited' && retryAfter !== null) description = `${description} ${t('errors:retryIn', { seconds: retryAfter })}`;
  return {
    kind,
    code,
    status: apiError?.status ?? 0,
    title: t(CREDENTIAL_CODES.includes(code) ? 'errors:titles.credentials' : `errors:titles.${kind}`),
    description,
    requestId: apiError?.requestId ?? null,
    retryAfter,
    params,
  };
}
